import { ReactElement, useCallback } from 'react';
import { Trans } from 'react-i18next';
import { RawTrack } from '@customer/store/apis/investigationsApi';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { ColDef, ColumnState } from 'ag-grid-community';
import RawTracksColumnDropdown from '../raw-track-columns/RawTracksColumnDropdown';
import styles from './InvestigationRawTracks.module.scss';
import useRawTrackColumnDefs from './useRawTrackColumnDefs';
import useRawTrackRows from './useRawTrackRows';
import { isEnumValue } from '@cas/utils/typeHelpers';
import { RawTrackColumns, setRawTrackCols } from '@customer/store/slices/investigationGuiSlice';
import useRawTrackRowSelection from './useRawTrackRowSelection';

const defaultColDef: ColDef<RawTrack> = { sortable: false, minWidth: 80 };

const getRowId = (data: RawTrack) => data.id.toString();

export const InvestigationRawTracks = (): ReactElement => {
  const dispatch = useAppDispatch();

  const view = useAppSelector(state => state.investigationGui.view);

  const columnDefs = useRawTrackColumnDefs();

  const displayTracks = useRawTrackRows();

  const onColumnMoved = useCallback(
    (columnState: ColumnState[]) => {
      const visibleCols: ColumnState[] = columnState.filter((col: ColumnState) => !col.hide);
      const visibleColIds: string[] = visibleCols.map(({ colId }) => colId);
      const rawTrackCols = visibleColIds.filter(
        colId => colId && isEnumValue(colId, RawTrackColumns),
      ) as RawTrackColumns[];

      dispatch(setRawTrackCols(rawTrackCols));
    },
    [dispatch],
  );

  const rowSelectionProps = useRawTrackRowSelection();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.rowCount}>
          <Trans
            i18nKey="RowCount"
            values={{ count: displayTracks.length }}
            components={{ b: <strong /> }}
          />
        </div>
        <div className={styles.dropdown}>
          <RawTracksColumnDropdown />
        </div>
      </div>
      <GridControlled<RawTrack>
        key={view}
        sliceKey={'InvestigationRawTracks'}
        rowData={displayTracks}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
        suppressHorizontalScroll={false}
        onColumnMoved={onColumnMoved}
        animateRows={false}
        {...rowSelectionProps}
      />
    </div>
  );
};

export default InvestigationRawTracks;
